import React, { useState, useContext, useRef, useMemo, useCallback, useEffect } from "react";
import { usePersistentState } from "../hooks/usePersistentState";
import { UserContext } from "./user";
import { HotelContext } from "./hotel";
import { CheckInContext } from "./checkin";
import { T } from "react-translator-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckSquare,
    faSquare
} from "@fortawesome/free-solid-svg-icons";
import { AdyenCheckout, Card, Dropin, PayPal, Twint } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import usePaymentsActions from "../hooks/usePaymentsActions";
import useCheckInActions from "../hooks/useCheckInActions";
import {
    PAYMENT_POLL_INTERVAL_MS,
    PAYMENT_POLL_MAX_ATTEMPTS,
    evaluatePendingPaymentResolution,
    getPendingTypesFromMakePaymentsResponse,
    hasMakePaymentsHardFailure,
    shouldStartPendingPoll,
} from "../helpers/paymentPending";
import { isGemunder, isOffgrid } from "../components/CheckIn/hotelConditions";
import { currencyFormat, slugify } from "../functions";
import { Form } from "react-bootstrap";

const PaymentContext = React.createContext()

const PAYMENT_STATUSES = {
    INITIAL: "INITIAL",
    COMPONENT_LOADING: "COMPONENT_LOADING",
    COMPONENT_LOADED: "COMPONENT_LOADED",
    COMPONENT_ERROR: "COMPONENT_ERROR",
    COMPONENT_MOUNTED: "COMPONENT_MOUNTED",
    IN_AUTHORIZATION: "IN_AUTHORIZATION",
    IN_AUTHORIZATION_DETAIL: "IN_AUTHORIZATION_DETAIL",
    AUTHORIZATION_OK: "AUTHORIZATION_OK",
    AUTHORIZATION_FAILED: "AUTHORIZATION_FAILED",
    MAKE_PAYMENTS: "MAKE_PAYMENTS",
    PAYMENT_FAILED: "PAYMENT_FAILED",
    PAYMENT_PENDING: "PAYMENT_PENDING",
    PAYMENT_OK: "PAYMENT_OK",
    NOTHING_TO_PAY: "NOTHING_TO_PAY"
}
const initialState = { sessionId: null, checkInId: null, cart: [], cartIdempotencyKey: null, currency: "USD", total: 0, componentSettings: null, status: PAYMENT_STATUSES.INITIAL, lastError: null };
const PAYMENT_STORAGE_TTL_MINUTES = 72000;

function persistPaymentOutcome(hotelId, patch) {
    if (!hotelId || typeof window === "undefined") {
        return;
    }
    const fullkey = `ViCo::${hotelId}_-_payment`;
    let current = {};
    try {
        const raw = window.localStorage.getItem(fullkey);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.value && typeof parsed.value === "object") {
                current = parsed.value;
            }
        }
    } catch (err) {
        console.error(err);
    }
    try {
        window.localStorage.setItem(fullkey, JSON.stringify({
            value: { ...current, ...patch },
            expiry: Date.now() + PAYMENT_STORAGE_TTL_MINUTES * 60 * 1000,
        }));
    } catch (err) {
        console.error(err);
    }
}

function PaymentProvider({children}) {
    const { data: checkIn } = useContext(CheckInContext);
    const { language } = useContext(UserContext);
    const { data: hotel } = useContext(HotelContext);
    const { sessionInit, event: eventAction } = usePaymentsActions()
    const { getAccount } = useCheckInActions()
    const checkoutRef = useRef(null);
    const dropinRef = useRef(null);
    const onReadyCallbackRef = useRef(null); // Ref para mantener el callback actualizado
    const dataRef = useRef(null); // Ref para mantener data actualizado en async

    const [ initialized, setInitialized ] = useState(false);
    const initializedRef = useRef(false);
    const [data, setData, setPartialData, getCurrentValueFromStore] = usePersistentState("payment", { ...initialState }, PAYMENT_STORAGE_TTL_MINUTES)
    const [ componentMounted, setComponentMounted] = useState(false);
    const [ active, _setActive ] = useState(false); // Trigger cuando entra al paso
    const [ showDebugInfo, setShowDebugInfo ] = useState(false);

    const [optionPayLater, setOptionPayLater] = useState(true);
    const [paymentOmmited, setPaymentOmmited] = useState(false);
    const [pendingPaymentTypes, setPendingPaymentTypes] = useState([]);
    const pendingPollAttemptRef = useRef(0);
    const pendingPollTimerRef = useRef(null);

    // const [sessionId, setSessionId] = useState(null);
    // const [status, setStatus] = useState(null);
    // const [cart, setCart] = useState([]);

    let _clientKey = 'test_MW2GQ44S2VGYXNSZDK52HLAZTIW73AQB';
    let _environment = "test";
    if (process.env.REACT_APP_APP_ENV === 'prd') {
        _environment = "live";
        _clientKey = 'live_TXKPUEAYHNG2FAM53IVHRG3ZWEL5W4H5'     // hostz
        if (hotel?.id === "9324d1b3-c49b-45fd-8a4e-d48bb5c48516") {     // GEMUNDER
            _clientKey = 'live_TZP7AH5IQNC5XHP2VDQQLIFQ6MK565EY'
        }
        if (isOffgrid(hotel?.id)) {     // offgrid
            _clientKey = 'live_XEW4GYN3YNAX5DF52PQZFIE3OIO6D5MV'
        }
    }

    const setActive = (active) => {
        if (!active && initializedRef?.current) {
            initializedRef.current = null;
            setInitialized(false);
        }
        setTimeout(() => {
            _setActive(active);
        }, 1000);
    }

    useEffect(() => {
        const isAuthorizationInFlight =
            data.status === PAYMENT_STATUSES.IN_AUTHORIZATION
            || data.status === PAYMENT_STATUSES.IN_AUTHORIZATION_DETAIL;

        if (isAuthorizationInFlight && dropinRef.current) {
            return;
        }

        if (active && !initializedRef.current) {
            initializedRef.current = true;
            setInitialized(true);

            if (isAuthorizationInFlight) {
                setError(data?.lastError || T("payments.payment-not-authorised"));
                setStatus(PAYMENT_STATUSES.AUTHORIZATION_FAILED);
                return;
            }

            if ((data.status === PAYMENT_STATUSES.INITIAL || data.status === PAYMENT_STATUSES.NOTHING_TO_PAY) && data?.total <= 0) {
                console.log('7dic INITIALIZING 3 - NOTHING TO PAY');
                setStatus(PAYMENT_STATUSES.NOTHING_TO_PAY);
                return;
            }

            // if (!checkoutRef?.current) return;
            // if (dropinRef?.current) return;
            // console.log('4dic INITIALIZING 03');
            // if (data?.status === PAYMENT_STATUSES.INITIAL) return;
            // console.log('4dic INITIALIZING 04');
            // if (!data.componentSettings) return;
            // console.log('4dic INITIALIZING 05');

            let do_reset = true;
    
            if (data.status === PAYMENT_STATUSES.COMPONENT_MOUNTED) {
                initDropin(data.componentSettings);
                do_reset = false;
            }
    
            if (data.status === PAYMENT_STATUSES.AUTHORIZATION_FAILED || data.status === PAYMENT_STATUSES.PAYMENT_FAILED) {
                initDropin(data.componentSettings).then(comp => {
                    setTimeout(() => {
                        if (comp) {
                            comp.setStatus("error", {
                                message: data?.lastError || "Error. Please try again."
                            });
                        }
                    }, 100);
                });
                do_reset = false;
            }

            if (data.status === PAYMENT_STATUSES.PAYMENT_OK) {
                initDropin(data.componentSettings).then(comp => {
                    setTimeout(() => {
                        if (comp) {
                            comp.setStatus("success");
                        }
                    }, 100);
                });
                do_reset = false;
            }

            if (do_reset) {
                reset();
            }
        }

    }, [ data, active, checkoutRef?.current, dropinRef?.current ]);

    useEffect(() => {
        if (checkIn?.id !== data?.checkInId) {
            setPartialData(initialState);
            reset();
        }
    }, [ checkIn?.id, data?.checkInId ]);


    // useEffect(() => {
    //     console.log('1dic - AAA - useEffect - checkoutRef?.current', checkoutRef?.current);
    //     console.log('1dic - AAA - useEffect - paymentStepActive', paymentStepActive);

    //     if ([PAYMENT_STATUSES.COMPONENT_ERROR].includes(data.status) && checkoutRef?.current) {
    //         console.log('1dic - AAA - useEffect - initControl');
    //         _initControl();
    //     } else {
    //         // if ([PAYMENT_STATUSES.COMPONENT_LOADED, 
    //         //     PAYMENT_STATUSES.COMPONENT_MOUNTED,
    //         //     PAYMENT_STATUSES.AUTHORIZATION_FAILED,
    //         //     PAYMENT_STATUSES.PAYMENT_FAILED,
    //         //     PAYMENT_STATUSES.PAYMENT_OK
    //         // ].includes(data.status)) {
    //         //     console.log('1dic - AAA - useEffect - 0000');
    //         //     if (checkoutRef?.current && data?.componentSettings && paymentStepActive) {
    //         //         console.log('1dic - AAA - useEffect - initDropin');
    //         //         initDropin(data.componentSettings);
    //         //     }

    //         // }


    //     } 
        
    //     // if (data.status === PAYMENT_STATUSES.COMPONENT_LOADED && checkoutRef?.current && data?.componentSettings) {
    //     //     console.log('1dic - AAA - useEffect - initDropin');
    //     //     initDropin(data.componentSettings);
    //     // } else if (data.status === PAYMENT_STATUSES.COMPONENT_MOUNTED && !dropinRef?.currect && checkoutRef?.current && data?.componentSettings) {
    //     //     console.log('1dic - AAA - useEffect - initDropin');
    //     //     initDropin(data.componentSettings);
    //     // } else {
    //     //     console.log('1dic - AAA - useEffect - PONER INIT');
    //     //     // reset();
    //     // }
    // }, [ data.status, data.componentSettings, checkoutRef?.current, dropinRef?.current, paymentStepActive ]);

    const setPaymentCheckoutRef = (node) => {
        checkoutRef.current = node;
    };

    // Mantener dataRef actualizado para usarlo en callbacks async
    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const countryCode = useMemo(() => {
        const cc = checkIn?.passengers?.[0]?.residence || checkIn?.passengers?.[0]?.nationality || null;
        return cc || "CH";
    }, [checkIn?.passengers])

    const locale = useMemo(() => {
        let _locale = 'en-US';
        if (language === 'de') {
            _locale = 'de-DE';
        }
        if (language === 'fr') {
            _locale = 'fr-FR';
        }
        if (language === 'it') {
            _locale = 'it-IT';
        }
        if (language === 'es') {
            _locale = 'es-ES';
        }
        return _locale
    }, [countryCode, language])

    const paymentCurrency = useMemo(() => {
        if (checkIn?.balance?.currency) {
            return checkIn.balance.currency
        }
        return "CHF"
    }, [checkIn]);

    const allowSkipPayment = useMemo(() => {
        return data?.status === PAYMENT_STATUSES.NOTHING_TO_PAY
            || data?.status === PAYMENT_STATUSES.PAYMENT_OK
            || paymentOmmited;
    }, [data?.status, paymentOmmited]);

    const paymentDetail = useMemo(() => {
        let _cart = {
            checkInId: checkIn.id,
            reference: 'Payment',
            name: '.',
            description: '..',
            amount: 0,
            currency: paymentCurrency,
            amount: 0,
            amounts: {
                security_deposit: { value: 0, currency: paymentCurrency },
                webcheckin: { value: 0, currency: paymentCurrency },
                paymentscreen: { value: 0, currency: paymentCurrency },
                add_ons: { value: 0, currency: paymentCurrency }
            },
            add_ons: null
        }
        let _cartToSession = []
    
        const upsellingMode = false;

        let _total = 0;
        if (data?.cart && data?.cart.length > 0) {
        
            if (!upsellingMode) {
                _cart.amount = data.cart
                    .filter(item => item.selected && !item.isSecurityDeposit)
                    .reduce((sum, item) => sum + (100 * item.amount), 0);
        
                _cart.amounts.webcheckin.value = _cart.amount;
                _cartToSession.push({
                    type: "webcheckin",
                    detail: {
                        value: _cart.amount,
                        currency: paymentCurrency
                    }
                })
        
                console.log('HOY - _cart filter isSecurityDeposit', data.cart.filter(item => item.isSecurityDeposit).length);
                const itemsSD = data.cart.filter(item => item.isSecurityDeposit);
                if (itemsSD.length == 1) {
                    _cart.chargeDepositGuarantee = true;
        
                    // Condición único item en el carro
                    _cart.onlyDepositGuarantee = data.cart.filter(item => item.selected).length == 1;
                    // Condición, items en carro pero la suma es cero
                    _cart.onlyDepositGuarantee = _cart.onlyDepositGuarantee || (_cart.amount == 0);
        
                    const _sd_value = 100 * itemsSD[0].amount;
                    _cart.amounts.security_deposit.value = _sd_value;
                    _cartToSession.push({
                        type: "security_deposit",
                        detail: {
                            value: _sd_value,
                            currency: paymentCurrency
                        }
                    })
                } else {
                    _cart.chargeDepositGuarantee = false;
                    _cart.onlyDepositGuarantee = false;
                    _cart.amounts.security_deposit.value = 0;
                }
        
                const itemsUpsellings = data.cart.filter(item => item.isUpselling);
                if (itemsUpsellings) {
                    itemsUpsellings.forEach(add_on => {
                        console.log('11nov - add_on', add_on);
                        _cart.add_ons = add_on.descList;
                        _cart.amounts.add_ons.value = add_on.amount * 100;
                        _cart.amounts.webcheckin.value -= add_on.amount * 100;
                        if (_cartToSession[0]?.type === "webcheckin") {
                            _cartToSession[0].detail.value -= add_on.amount * 100;
                        }

                        if (_cart.add_ons.length > 0) {
                            _cart.add_ons.forEach(add_on_item => {
                                _cartToSession.push({
                                    type: "add_on",
                                    detail: {
                                        internalKey: add_on_item.internalKey,
                                        slug: add_on_item.slug,
                                        value: add_on_item.amount.value,
                                        currency: add_on_item.amount.currency,
                                        name: add_on_item.name
                                    }
                                })
                            })
                        }
                    })
                }
        
                // Muestro el monto total (porque solo es SD)
                if (_cart.onlyDepositGuarantee) {
                    _cart.amount = 100 * itemsSD[0].amount;
                }
        
                // No envío valores negativos
                if (_cart.amounts.webcheckin.value < 0) {
                    _cart.amounts.webcheckin.value = 0;
                    if (_cartToSession[0]?.type === "webcheckin") {
                        _cartToSession[0].detail.value = 0;
                    }
                }

                _total = _cartToSession.reduce((sum, item) => sum + (item.detail.value / 100), 0);
                setPartialData({ currency: paymentCurrency, total: _total });
            }
        }
        const _resp = { cart: _cart, total: _total, currency: paymentCurrency, cartToSession: _cartToSession }
        return _resp;
    }, [ data?.cart, data?.cartIdempotencyKey, checkIn?.id, paymentCurrency ]);

    const reset = () => {
        if (pendingPollTimerRef.current) {
            clearTimeout(pendingPollTimerRef.current);
            pendingPollTimerRef.current = null;
        }
        pendingPollAttemptRef.current = 0;
        setPendingPaymentTypes([]);
        if (dropinRef?.current) {
            dropinRef.current.unmount();
            dropinRef.current = null;
        }
        setPartialData({ sessionId: null, status: PAYMENT_STATUSES.INITIAL, lastError: null });
    }

    const setCart = (cart) => {
        if (cart.length > 0) {
            console.log('4dic1_setCart - cart', cart);
            setPartialData({ cart: cart, cartIdempotencyKey: _getCartIdempotencyKey(cart) });
        }
    }

    const _getCartIdempotencyKey = (cart) => {
        let _cart_rows = [];
        cart.map(item => {
            let _name = item.name;
            if (typeof _name === "object") {
                _name = _name[0];
            }
            if (_name) {
                _name = slugify(_name);
            }
            _cart_rows.push(`${_name}-${item?.qty || 1}-${item?.amount || 0}`)
        });
        return _cart_rows.join("|");
    }

    const setStatus = (status) => {
        if (!Object.values(PAYMENT_STATUSES).includes(status)) {
            return;
        }
        setPartialData({ status: status });
    }

    const setError = (error) => {
        console.log('setError:', error);
        setPartialData({ lastError: error });
    }

    const stopPendingPoll = useCallback(() => {
        if (pendingPollTimerRef.current) {
            clearTimeout(pendingPollTimerRef.current);
            pendingPollTimerRef.current = null;
        }
        pendingPollAttemptRef.current = 0;
    }, []);

    const applyMakePaymentsOutcome = useCallback((mkResponse, dropin = null) => {
        if (shouldStartPendingPoll(mkResponse)) {
            const types = getPendingTypesFromMakePaymentsResponse(mkResponse);
            setPendingPaymentTypes(types);
            setError(null);
            setStatus(PAYMENT_STATUSES.PAYMENT_PENDING);
            if (dropin?.setStatus) {
                dropin.setStatus("loading");
            }
            return { pending: true, types };
        }

        setPendingPaymentTypes([]);
        if (hasMakePaymentsHardFailure(mkResponse)) {
            const refusalReason = String(mkResponse?.refusalReason || "");
            const canContinueWithDeferredPayment =
                refusalReason.toLowerCase() === "payment account creation failed";
            const errorMsg = canContinueWithDeferredPayment
                ? T("payments.payment-unavailable-continue")
                : (mkResponse?.refusalReasonDetailed || mkResponse?.refusalReason || T("payments.payment-not-authorised"));

            if (canContinueWithDeferredPayment) {
                setPaymentOmmited(true);
            }
            setError(errorMsg);
            setStatus(PAYMENT_STATUSES.PAYMENT_FAILED);
            if (dropin?.setStatus) {
                dropin.setStatus("error", { message: errorMsg });
            }
            return { pending: false, ok: false, errorMsg };
        }

        setStatus(PAYMENT_STATUSES.PAYMENT_OK);
        if (dropin?.setStatus) {
            dropin.setStatus("success");
        }
        return { pending: false, ok: true };
    }, [setStatus, setPaymentOmmited]);

    const pollPendingPayments = useCallback(async (types, onContinue) => {
        if (!hotel?.id || !checkIn?.id) {
            return;
        }
        pendingPollAttemptRef.current += 1;
        try {
            const account = await getAccount(hotel.id, checkIn.id);
            if (!account) {
                if (onContinue) {
                    onContinue();
                }
                return;
            }
            const resolution = evaluatePendingPaymentResolution(account, types);
            if (!resolution.resolved) {
                if (pendingPollAttemptRef.current >= PAYMENT_POLL_MAX_ATTEMPTS) {
                    const timeoutMsg = T("payments.pending-timeout");
                    setError(timeoutMsg);
                    setStatus(PAYMENT_STATUSES.PAYMENT_FAILED);
                    stopPendingPoll();
                    dropinRef.current?.setStatus?.("error", { message: timeoutMsg });
                    return;
                }
                if (onContinue) {
                    onContinue();
                }
                return;
            }
            stopPendingPoll();
            if (resolution.ok) {
                setError(null);
                setStatus(PAYMENT_STATUSES.PAYMENT_OK);
                dropinRef.current?.setStatus?.("success");
            } else {
                const failMsg = resolution.reason || T("payments.payment-not-authorised");
                setError(failMsg);
                setStatus(PAYMENT_STATUSES.PAYMENT_FAILED);
                dropinRef.current?.setStatus?.("error", { message: failMsg });
            }
        } catch (err) {
            console.error("payment pending poll error", err);
            if (onContinue) {
                onContinue();
            }
        }
    }, [hotel?.id, checkIn?.id, getAccount, stopPendingPoll]);

    useEffect(() => {
        if (data?.status !== PAYMENT_STATUSES.PAYMENT_PENDING) {
            stopPendingPoll();
            return undefined;
        }

        const types = pendingPaymentTypes.length
            ? pendingPaymentTypes
            : ["security_deposit"];

        let cancelled = false;
        const scheduleNext = () => {
            pendingPollTimerRef.current = setTimeout(() => {
                if (cancelled) {
                    return;
                }
                pollPendingPayments(types, scheduleNext);
            }, PAYMENT_POLL_INTERVAL_MS);
        };

        pollPendingPayments(types, scheduleNext);

        return () => {
            cancelled = true;
            stopPendingPoll();
        };
    }, [data?.status, pendingPaymentTypes, pollPendingPayments, stopPendingPoll]);

    const handleChangePaymentState = (state) => {
        console.log('handleChangePaymentState', state);
    }

    const handleChangeOmmited = (state) => {
        console.log('handleChangeOmmited', state);
    }

    const showPaymentComponent = useMemo(() => {
        return ["in-progress", "error", "done"].includes(data.status)
    }, [ data.status ]);

    const _initControl = useCallback(async () => {
        console.log('1dic - **** - initControl', dropinRef?.current);

        if (dropinRef?.current) {
            // Ya está montado, resetear a estado ready si está en error
            dropinRef.current.setStatus("loading");
            setPartialData({ status: PAYMENT_STATUSES.COMPONENT_MOUNTED });
            setTimeout(() => {
                dropinRef.current.setStatus("ready");
            }, 2000);
            return;
        }
        
        setPartialData({ status: PAYMENT_STATUSES.COMPONENT_LOADING });

        const payload = {
            ...paymentDetail.cart,
            cart: paymentDetail.cartToSession,
            countryCode: countryCode,
            locale: locale
        }
        // console.log('29ene - PAYMENT DETAIL', paymentDetail)
        const r = await sessionInit(payload);
        if (r && r.internalId) {
            setPartialData({ sessionId: r.internalId, checkInId: checkIn?.id, componentSettings: r, status: PAYMENT_STATUSES.COMPONENT_LOADED });
        } else {
            setPartialData({ status: PAYMENT_STATUSES.COMPONENT_ERROR });
        }
        initDropin(r);

    }, [paymentDetail, countryCode, locale, setPartialData]);

    const initControl = useCallback(async () => {
        _initControl();
    }, [ _initControl ]);

    const onReadyControl = useCallback(() => {
        console.log('5dic - onReadyControl - data.status', data.status);
        // if (data.status === PAYMENT_STATUSES.PAYMENT_FAILED) {
        //     setTimeout(() => {
        //         dropinRef.current?.setStatus("error", {
        //             message: data?.lastError || "Payment failed. Please try again."
        //         });
        //     }, 50);
        // } if (data.status === PAYMENT_STATUSES.PAYMENT_OK) { 
        //     setTimeout(() => {
        //         dropinRef.current?.setStatus("success");
        //     }, 50);
        // } else {
        //     setPartialData({ status: PAYMENT_STATUSES.COMPONENT_MOUNTED });
        // }
    
        setComponentMounted(true);
    }, [ data?.status, data?.lastError ]);

    // Actualizar el ref cada vez que onReadyControl cambia
    useEffect(() => {
        onReadyCallbackRef.current = onReadyControl;
    }, [onReadyControl]);

    const event = useCallback(async (evt, payload) => {
        const _data = dataRef?.current || data;

        if (evt === "pre-authorization") {
            setStatus(PAYMENT_STATUSES.IN_AUTHORIZATION);
        }
        if (evt === "pre-authorization-details") {
            if (_data?.status !== PAYMENT_STATUSES.IN_AUTHORIZATION) {
                setStatus(PAYMENT_STATUSES.AUTHORIZATION_FAILED);
                return;
            }
            setStatus(PAYMENT_STATUSES.IN_AUTHORIZATION_DETAIL);
        }
        const r_action = await eventAction(_data.sessionId, evt, payload);

        if (evt === "pre-authorization") {
            if (r_action.status) {
                if (!r_action.action) {
                    setStatus(PAYMENT_STATUSES.AUTHORIZATION_OK);
                }
            } else {
                setStatus(PAYMENT_STATUSES.AUTHORIZATION_FAILED);
                setError(r_action.detail || "Payment not authorised");
            }
        }

        if (evt === "pre-authorization-details") {
            if (r_action.status) {
                setStatus(PAYMENT_STATUSES.AUTHORIZATION_OK);
            } else {
                setStatus(PAYMENT_STATUSES.AUTHORIZATION_FAILED);
                setError(r_action.detail || "Payment not authorised");
            }
        }
        if (evt === "make-payments") {
            applyMakePaymentsOutcome(r_action);
        }

        return r_action;
    }, [eventAction, applyMakePaymentsOutcome, data, dataRef]);

    const initDropin = useCallback(async (settings) => {
        console.log('5dic - AAA - initDropin - data.status', data.status);
        console.log('5dic - AAA - checkoutRef.current', checkoutRef.current);
        
        if (!checkoutRef.current) {
            console.log('5dic - AAA - checkoutRef.current not found');
            return;
        }
        const _paymentMethods = {
            "paymentMethods": []
        }
        if (settings.paymentMethods?.paymentMethods) {
            settings.paymentMethods?.paymentMethods.map(pm => {
                if (isGemunder(hotel?.id) && localStorage.getItem('all-payment-methods')) {
                    _paymentMethods.paymentMethods.push(pm)
                } else {
                    if (pm.name === "Cards") {
                        _paymentMethods.paymentMethods.push(pm)
                    }
                }
            })
        }

        let globalConfiguration = {
            environment: _environment,
            clientKey: _clientKey,
            locale: settings.shopperLocale,
            session: {
                id: settings.id,
                sessionData: settings.sessionData
            },
            analytics: {
                enabled: true,
            },
    
            shopperLocale: locale,
            countryCode: countryCode,
    
            paymentMethodComponents: {
                scheme: [ Card ]
            },
            paymentMethodsResponse: _paymentMethods,
            // paymentMethodsConfiguration: {
            //     scheme: {
            //         // Configuración específica para tarjetas
            //         showPayButton: true, // Muestra el botón de pagar
            //         hasHolderName: true, // Permite ingresar el nombre del titular
            //         holderNameRequired: true, // Hace obligatorio el nombre del titular
            //         billingAddressRequired: false, // Opcional: dirección de facturación
            //     },
            // },
            onSubmit: async (state, component, actions) => {
                console.log('11nov - onSubmit', state, component, actions);
                try {
                    dropinRef.current.setStatus("loading");
                    let payload = { ...state, sessionInternalId: settings.internalId }
                    console.log('2dic - payload', payload);
                    const preAuthorizationResult = await event("pre-authorization", payload);
                    console.log('2dic - preAuthorizationResult => ', preAuthorizationResult)

                    if (preAuthorizationResult.status) {
                        if (preAuthorizationResult.action) {
                            dropinRef.current.handleAction(preAuthorizationResult.action);
                        } else {
                            console.log('**29nov**** - onSubmit 4 - preAuthorizationResult.resultCode', preAuthorizationResult.resultCode);
                            const mk_response = await event("make-payments");
                            applyMakePaymentsOutcome(mk_response, dropinRef.current);
                        }
                    } else {
                        dropinRef.current.setStatus("error", {
                            message: preAuthorizationResult.detail || data.lastError || "Payment not authorised"
                        });
                    }

                    // if (preAuthorizationResult.action) {
                    //     // Si la acción es 'redirect' (RedirectShopper), el Drop-in
                    //     // automáticamente redirigirá al comprador al banco.
                    //     component.handleAction(preAuthorizationResult.action); 
                    // } else {
                    //     console.log('**29nov**** - onSubmit 2 - state', state);
                    //     console.log('**29nov**** - onSubmit 3 - component', component);
                    //     console.log('**29nov**** - onSubmit 3 - actions', actions);
                    //     // await makePayments();
                    //     // Maneja el resultado final
                    //     // handleFinalResult(response);
                    // }
    
    
    
                    // if (paymentDetail.amounts) {
                    //     payload.amounts = paymentDetail.amounts;
                    // }
                    // if (paymentDetail.chargeDepositGuarantee) {
                    //     payload.chargeDepositGuarantee = true;
                    // }
                    // if (paymentDetail.onlyDepositGuarantee) {
                    //     payload.onlyDepositGuarantee = true;
                    // }
                    // if (paymentDetail.add_ons) {
                    //     payload.add_ons = paymentDetail.add_ons;
                    // }
    
                    // // Make a POST /payments request from your server.
                    // const result = await paymentEvent(internalId, "submit", payload);
                    // console.log('1010 - RRRR => ', result)
                    // const result = await makePaymentsCall(state.data, countryCode, locale, amount);
                } catch (error) {
                    console.log("onSubmit", error);
                    console.log("Respuesta completa del servidor:", error.response || error);
                    const errorMsg = error.response?.data?.message || error.message || "Payment error";
                    setError(errorMsg);
                    actions.reject();
                }
            },
            onPaymentCompleted: (result, component) => {
                console.log('5dic - PAYMENT - onPaymentCompleted', result);
                // paymentEvent(internalId, "completed", result);
            },
            onPaymentFailed: (result, component) => {
                console.log('5dic - PAYMENT - onPaymentFailed', result);
                // paymentEvent(internalId, "failed", result);
            },
            onError: (error, component) => {
                console.log('5dic - PAYMENT - onError', error);
                console.error(error.name, error.message, error.stack, component);
                // showResult(true, error);
            }
        }

        try {
            const checkout = await AdyenCheckout(globalConfiguration);
            const dropinConfiguration = {
                // Required if you import individual payment methods.
                paymentMethodComponents: [Card, PayPal, Twint],
                // Optional configuration.
                instantPaymentTypes: ['applepay', 'googlepay'],
                onLoad: () => {
                    console.log('5dic - onLoad 1');
                },
                onReady: () => {
                    console.log('5dic - onReady 1');
                    console.log('5dic - onReadyCallbackRef.current', onReadyCallbackRef.current);
                    // Usar el ref para siempre tener el callback actualizado
                    // console.log('1dic - onReady ejecutando, data.status actual:', data.status);
                    if (onReadyCallbackRef.current) {
                        console.log('5dic - going to call onReadyCallbackRef.current');
                        onReadyCallbackRef.current();
                    }
                }
            };

            console.log('5dic - checkout', checkout)
            console.log('5dic - checkoutRef.current', checkoutRef.current)
            dropinRef.current = new Dropin(checkout, dropinConfiguration).mount(checkoutRef.current);
            console.log('5dic - checkoutRef.current', checkoutRef.current)
            console.log('5dic - checkoutRef.current.innerHTML', checkoutRef.current?.innerHTML);

            console.log('5dic - dropinRef.current', dropinRef.current)

            // setDropinInstance(dropin); // Guardar la nueva instancia
            // paymentResult("InProgress", null);
            // setLoading(false);

            return dropinRef.current;

        } catch (err) {
            console.log('5dic', err);
            console.log('5dic - err', err);
            const errorMsg = err.message || "Error initializing payment component";
            setError(errorMsg);
            setPartialData({ status: PAYMENT_STATUSES.COMPONENT_ERROR });
            // setLoading(false);
            // showResult(true, err);
            return;
        }
    }, [countryCode, locale, data?.status, hotel?.id, event, applyMakePaymentsOutcome]);

    // const PaymentComponent = useMemo(() => {
    //     return function PaymentComponentInner() {
    //       return (
    //         <div className="payment-wrapper" style={{ position: 'relative' }}>
    //           {/* El dropin siempre existe, pero está oculto hasta usarlo */}
    //           <div
    //             ref={checkoutRef}
    //             style={{
    //               minHeight: 400,
    //               display: dropinMounted ? 'block' : 'none',
    //             }}
    //           />
    
    //           {/* Carrito + botón inicial */}
    //           {!dropinMounted && (
    //             <div>
    //               <CartVisualizer />
    //               <button
    //                 className="btn btn-primary mt-3"
    //                 onClick={async () => {
    //                 //   setLoading(true);
    //                   handlePaymentClick();
    //                 //   await initDropin({ id: 'SESSION_ID', sessionData: 'SESSION_DATA' });
    //                 //   setLoading(false);
    //                 }}
    //               >
    //                 Pagar ahora
    //               </button>
    //             </div>
    //           )}
    
    //           {loading && (
    //             <div
    //               style={{
    //                 position: 'absolute',
    //                 inset: 0,
    //                 background: 'rgba(255,255,255,0.7)',
    //                 display: 'flex',
    //                 alignItems: 'center',
    //                 justifyContent: 'center',
    //                 zIndex: 10,
    //               }}
    //             >
    //               <div className="spinner-border" />
    //             </div>
    //           )}
    //         </div>
    //       );
    //     };
    //   }, [dropinMounted, loading]);

    // const component = useCallback(({ optionPayLater }) => {
    //     return (<div>
    //         {/* <div className="payment-container mb-4">
    //             <div ref={checkoutRef} className="payment" style={{ display: (showPaymentComponent ? 'block' : 'none') }}>
    //             </div>
    //         </div> */}
    //         {!loading && <div>
    //             <p>SessionId: {data.sessionId}</p>
    //             <p>Status: {data.status}</p>
    //             <p>((((((((</p>
    //             <CartVisualizer checkInId={checkIn.id} currency={paymentCurrency} cart={data.cart} />
    //             <button className="px-1 mt-2 text-uppercase btn btn-primary btn-block btn-lg" onClick={handlePaymentClick}>{data.status !== "error" ? T("payments.pay") : T("retry-pay")}</button>
    //             <p>)))))))</p>
    //         </div>}
    //         {loading && <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "200px" }}>
    //             <div className="spinner-border"></div>
    //             <p className="p-2 text-center">{T("payments.loading-component")}</p>
    //         </div>}
    //     </div>)
    // }, [ loading ]);

    // const [notification, setNotification] = useState(null);
    // const [notificationText, setNotificationText] = useState(null);

    // const success = (text) => {
    //     window.scroll(0, 0);
    //     setNotificationText(text);
    //     setNotification(STATES.SUCCESS);
    // }
    
    // const error = (text) => {
    //     window.scroll(0, 0);
    //     setNotificationText(text);
    //     setNotification(STATES.ERROR);
    // }

    // const PaymentControls = ({ checkoutRef = null, currentStep = null }) => {
    //     const [ checkoutRefState, setCheckoutRefState ] = useState(0);

    //     useEffect(() => {
    //         console.log('**28nov** - ????????????????', checkoutRef?.current);
    //         setCheckoutRefState(prev => prev + 1);
    //     }, [ checkoutRef?.current ])

    //     useEffect(() => {
    //         setForceRefresh(prev => {
    //             console.log('**28nov** - prev => ', prev, 'currentStep => ', currentStep);
    //             if (prev < 13) {
    //                 return prev + 1;
    //             }
    //             return prev;
    //         })
    //     }, [ currentStep ])

    //     const paymentButtonLabel = useMemo(() => {
    //         return data.status === PAYMENT_STATUSES.INITIAL ? T("payments.pay") : T("payments.retry-pay");
    //     }, [data.status]);
    
    //     const showPaymentButton = useMemo(() => {
    //         let _show = true;
    //         _show = _show && checkoutRef?.current;

    //         if (componentLoadedButNotMounted) {
    //             _show = _show && componentLoadedButNotMounted;
    //         } else {
    //             _show = _show && (data.status === PAYMENT_STATUSES.INITIAL || data.status === PAYMENT_STATUSES.AUTHORIZATION_FAILED);
    //         }
            
    //         console.log('showPaymentButton - checkoutRef?.current', checkoutRef?.current);
    //         console.log('showPaymentButton - data.status', data.status);
    //         console.log('showPaymentButton - componentLoadedButNotMounted', componentLoadedButNotMounted);
    //         console.log('showPaymentButton - RESULT', _show);
    
    //         return _show;
    //     }, [ checkoutRef?.current, data.status ]);
    
    //     const errorToShow = useMemo(() => {
    //         return data.status === PAYMENT_STATUSES.AUTHORIZATION_FAILED ? "Error al autorizar el pago" : null;
    //     }, [data.status]);

    //     return (
    //       <div>
    //         {forceRefresh}
    //         {checkoutRefState}
    //         {showCartVisualizer && <CartVisualizer checkInId={checkIn.id} currency={paymentCurrency} cart={data.cart} />}
    //         {errorToShow && <div className="alert alert-danger text-center" style={{ marginTop: 10 }}>
    //             <p className="text-danger mb-0">{errorToShow}</p>
    //         </div>}
    //         {!checkoutRef?.current && <p className="text-danger">Error: Payment Component without container!</p>}
    //         {showPaymentButton && <button
    //             className="btn btn-primary btn-block btn-lg"
    //             disabled={loading}
    //             onClick={() => initControl()}
    //         >
    //             {paymentButtonLabel}
    //         </button>}
    //         <div className="alert alert-info text-center" style={{ marginTop: 10 }}>
    //             <p className="mb-0 font-size-sm">data.sessionId: {data.sessionId}</p>
    //             <p className="mb-0 font-size-sm">data.status: {data.status}</p>
    //             <button className="btn btn-primary btn-block btn-md mt-2" onClick={() => {
    //                     setLoading(false);
    //                     setPartialData({ sessionId: null, status: PAYMENT_STATUSES.INITIAL });                
    //             }}>
    //                 Reset
    //             </button>
    //             <button className="btn btn-primary btn-block btn-md mt-2" onClick={() => {
    //                 setCheckoutRefState(prev => prev + 1);
    //             }}>
    //                 Force Re-render
    //             </button>
    //         </div>
    //       </div>
    //     );
    //   };

      return <PaymentContext.Provider value={{
        sessionId: data.sessionId || null, 
        initialized: initialized,
        cart: data.cart,
        total: data.total, 
        currency: data.currency,
        setCart, 
        setPaymentCheckoutRef, 
        PaymentControls, 
        status: data.status, 
        lastError: data.lastError,
        initControl,
        setError,
        event,
        reset,
        setActive: setActive,
        setOptionPayLater: setOptionPayLater,
        optionPayLater: optionPayLater,
        paymentOmmited: paymentOmmited,
        setPaymentOmmited: setPaymentOmmited,
        showDebugInfo: showDebugInfo,
        hasCheckoutRef: !!(checkoutRef?.current || null),
        hasComponentRef: !!(dropinRef?.current || null),
        componentIsMounted: componentMounted,
        dropinVisible: componentMounted && !!(dropinRef?.current || null),
        allowSkipPayment: allowSkipPayment,
        pendingPaymentTypes,
    }}>
        {children}
    </PaymentContext.Provider>
}

const PaymentButton = () => {
    const { status, initControl, optionPayLater, paymentOmmited, setPaymentOmmited } = usePaymentContext();

    const paymentButtonLabel = useMemo(() => {
        return status === PAYMENT_STATUSES.INITIAL || status === PAYMENT_STATUSES.COMPONENT_LOADING || status === PAYMENT_STATUSES.COMPONENT_LOADED ? T("payments.pay") : T("payments.retry-pay");
    }, [status]);

    return <>
        <button
            className="btn btn-block btn-lg mt-3 py-2 payment-button"
            style={{ fontSize: 16, backgroundColor: "#6e4387", border: "none", color: "white" }}
            onClick={() => initControl() }
        >
            {paymentButtonLabel}
        </button>
        {optionPayLater && <legend className="mt-2 mb-0">
            <Form.Check inline id="payment-ommited">
                <Form.Check.Input
                    id={`payment-ommited`}
                    className="ml-1 mr-2 custom-checkbox"
                    checked={paymentOmmited || false}
                    onChange={(event) =>
                        setPaymentOmmited(event.target.checked)
                    }
                />
                <Form.Check.Label className="font-size-sm text-left paymentButtonComponent" style={{ fontSize: "0.95rem" }}>
                    {T("payments.later")}
                </Form.Check.Label>
            </Form.Check>
        </legend>}
    </>
}

const CartVisualizer = ({ showPaymentButton = false }) => {
    const { data: checkIn } = useContext(CheckInContext);
    const { cart, currency, total, optionPayLater, paymentOmmited, setPaymentOmmited } = usePaymentContext();

    if (cart?.length === 0 || total === 0) {
        return (<p className="text-center p-4">{T('payments.balance-paid')}</p>);
    }

    return <div>
        {cart?.length > 0 && (
            <p className="pt-2 general-text-sm text-center">{T("payments.items-pay-with-deposit")}</p>
        )}

        <div className="card border-default p-3" style={{ borderRadius: 8 }}>
            <div className="border-bottom pb-2 mb-2">
                <h6 className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>{T("payments.summary-of-charges")}</h6>
            </div>
            {(cart && cart.map((el, idx) => {
                const isRequired = el.required || false; // cartRequired.includes(el.id);
                const inCart = el.selected || false; // cart.includes(el.id);
                return (
                    <div
                        key={`balance-detail-${idx}`}
                        className="text-center"
                        style={{
                            fontWeight: inCart ? "400" : "lighter",
                            fontSize: "90%",
                            padding: "5px 0"
                        }}
                        // onClick={() => {
                        //     if (isRequired) return;
                        //     toggleCart(el.id)
                        // }}
                        >
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex justify-content-between align-items-center">
                                {!isRequired && (
                                    <FontAwesomeIcon
                                        icon={inCart ? faCheckSquare : faSquare}
                                        style={{ fontSize: 16, color: inCart ? "#5b2e5c" : "#AAA" }}
                                        className="mr-2"
                                    />
                                )}
                                <div className="text-left" style={{ lineHeight: 1 }}>{el.name}
                                    {el.qty > 1 && (<small>{el.qty && ` (x${el.qty})`}</small>)}
                                </div>
                            </div>
                            <div>{el.amount.toFixed(2)} {currency}</div>
                        </div>
                        {el.desc && (<div className="text-left font-size-sm" style={{ lineHeight: 1 }}><small>{el.desc}</small></div>)}
                        {el.descList?.length > 0 && (
                            <div className="text-left font-size-sm border p-2 mt-2" style={{ lineHeight: 1, borderRadius: 8, borderColor: "#6e4287 !important", background: "#fbf3ff" }}>
                                {el.descList?.map((el1, idx1) => (
                                    <div key={`ups-desc-item-${idx1}`} style={{ lineHeight: 1.2 }}>
                                        <small>- {el1.coloquial}</small>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )
            }))}
            <div className="border-top pt-3 mt-2">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>{T("payments.total-to-pay")}</h6>
                    <h6 className="mb-0 font-weight-bold" style={{ fontSize: 16 }}>{total.toFixed(2)} {currency}</h6>
                </div>
            </div>
            {showPaymentButton && <PaymentButton />}
        </div>
    </div>
}

const PaymentControls = ({ optionPayLater, paymentOmmited, setPaymentOmmited }) => {
    const {
        sessionId,
        initialized,
        hasCheckoutRef,
        hasComponentRef,
        status,
        reset,
        componentIsMounted,
        total,
        showDebugInfo,
        paymentOmmited: paymentOmmitedFromContext,
        lastError,
        pendingPaymentTypes,
    } = usePaymentContext();

    const resetHandler = useCallback(() => {
        reset();
    }, [reset]);

    console.log('**29nov** - componentLoadedButNotMounted - componentIsMounted', componentIsMounted);

    // const componentLoadedButNotMounted = useMemo(() => {
    //     console.log('**29nov** - componentLoadedButNotMounted - status', status);
    //     console.log('**29nov** - componentLoadedButNotMounted - componentIsMounted', componentIsMounted);
    //     return status === PAYMENT_STATUSES.COMPONENT_LOADED && !componentIsMounted;
    // }, [ status, componentIsMounted ]);

    const showPaymentButton = useMemo(() => {
        const isInitialWithAmount = total > 0 && status === PAYMENT_STATUSES.INITIAL;
        const isAuthorizationRetry = status === PAYMENT_STATUSES.AUTHORIZATION_FAILED;
        const isPaymentRetry = status === PAYMENT_STATUSES.PAYMENT_FAILED && !paymentOmmitedFromContext;        
        return isInitialWithAmount || isAuthorizationRetry || isPaymentRetry;
    }, [status, total, paymentOmmitedFromContext]);

    // const errorToShow = useMemo(() => {
    //     return status === PAYMENT_STATUSES.AUTHORIZATION_FAILED ? "Error al autorizar el pago" : null;
    // }, [status]);

    const isPaymentPending = status === PAYMENT_STATUSES.PAYMENT_PENDING;

    const loading = useMemo(() => {
        if (isPaymentPending) return false;
        if (!initialized) return true;
        if (status === PAYMENT_STATUSES.COMPONENT_LOADING) return true;
        if (status === PAYMENT_STATUSES.COMPONENT_LOADED) {
            if (!componentIsMounted) {
                return true;
            }
        }
        return false;
    }, [initialized, status, componentIsMounted, hasComponentRef, isPaymentPending]);

    const showCartVisualizer = useMemo(() => {
        return !loading && (status === PAYMENT_STATUSES.INITIAL || status === PAYMENT_STATUSES.NOTHING_TO_PAY);
    }, [status, loading]);

    // console.log('1dic - **29nov**** - componentIsMounted', componentIsMounted);
    // console.log('1dic - **29nov**** - hasCheckoutRef', hasCheckoutRef);
    // console.log('1dic - **29nov**** - hasComponentRef', hasComponentRef);

    return (
        <div>
            {loading && <div className="d-flex justify-content-center align-items-center flex-column" style={{ minHeight: "200px" }}>
                <div className="spinner-border" style={{ width: "2rem", height: "2rem", color: "#B5B4A1" }}>
                    <span className="sr-only">{T("payments.loading-component")}</span>
                </div>
                <p className="p-2 m-2 text-center">{T("payments.loading-component")}</p>
            </div>}
            {isPaymentPending && (
                <div className="d-flex justify-content-center align-items-center flex-column py-4" style={{ minHeight: "200px" }}>
                    <div className="spinner-border" style={{ width: "2rem", height: "2rem", color: "#6e4387" }}>
                        <span className="sr-only">{T("payments.pending-confirmation")}</span>
                    </div>
                    <p className="p-2 m-2 text-center mb-1">{T("payments.pending-confirmation")}</p>
                </div>
            )}
            {lastError && (status === PAYMENT_STATUSES.PAYMENT_FAILED || status === PAYMENT_STATUSES.AUTHORIZATION_FAILED) && (
                <div className="alert alert-danger text-center mt-3 mb-0" role="alert">
                    <p className="text-danger mb-0 font-size-sm">{lastError}</p>
                </div>
            )}
            {showCartVisualizer && <CartVisualizer showPaymentButton={showPaymentButton} />}
            {/* {errorToShow && <div className="alert alert-danger text-center" style={{ marginTop: 10 }}>
                <p className="text-danger mb-0">{errorToShow}</p>
            </div>} */}
            {/* {!hasCheckoutRef && <p className="text-muted">Loading Component...</p>} */}
            {showPaymentButton && !showCartVisualizer && !isPaymentPending && <PaymentButton />}
            {showDebugInfo && <div className="alert alert-info text-center" style={{ marginTop: 10 }}>
                <p className="mb-0 font-size-sm">session: {sessionId}</p>
                <p className="mb-0 font-size-sm">status: {status}</p>
                <button className="btn btn-primary btn-block btn-md mt-2" onClick={() => {
                    resetHandler();
                }}>
                    Reset
                </button>
            </div>}
        </div>
    );
};

// const PaymentComponent = ({ checkoutRef, showPaymentComponent }) => {
//     console.log("Render DropinContainer");
//     return <div ref={checkoutRef} className="payment" style={{ display: (showPaymentComponent ? 'block' : 'none') }} />;
// }
const usePaymentContext = () => useContext(PaymentContext);
export { PaymentProvider, usePaymentContext, PAYMENT_STATUSES, PaymentControls, persistPaymentOutcome }